import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import i18n from "@/i18n";
import { getCurrentClinicCustomerId } from "@/lib/utils/clinic-context";
import { useCurrentClinicCustomerId } from "@/hooks/useCurrentClinicCustomerId";

export interface PackageSubtypeRow {
  id: string;
  name: string;
  customer_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface PackageSubtypeInsert {
  id?: string;
  name: string;
  customer_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

interface UsePackageSubtypesOptions {
  enabled?: boolean;
}

export const usePackageSubtypes = ({ enabled = true }: UsePackageSubtypesOptions = {}) => {
  const { data: customerId } = useCurrentClinicCustomerId();

  return useQuery({
    queryKey: ["package-subtypes", customerId],
    enabled: enabled && !!customerId,
    queryFn: async () => {
      if (!customerId) throw new Error("Nenhuma clínica selecionada");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("package_subtypes")
        .select("*")
        .order("name", { ascending: true })
        .or(`customer_id.is.null,customer_id.eq.${customerId}`);

      if (error) throw error;
      return (data ?? []) as PackageSubtypeRow[];
    },
  });
};

export const useCreatePackageSubtype = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: Omit<PackageSubtypeInsert, "customer_id">) => {
      const customerId = await getCurrentClinicCustomerId();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("package_subtypes")
        .insert({
          name: payload.name.trim(),
          customer_id: customerId,
        })
        .select()
        .single();

      if (error) throw error;
      return data as PackageSubtypeRow;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["package-subtypes"] });
      toast.success(i18n.t("erp:itemForm.groupToasts.created"));
    },
    onError: (error) => {
      console.error("Erro ao cadastrar subtipo de pacote:", error);
      toast.error(i18n.t("erp:itemForm.groupToasts.createError"));
    },
  });
};
